import ApplyV2Content from '@core/components/ApplyDialogV2/ApplyV2Content'
import type { ApplyV2SettingsSectionName } from '@core/app/types/applyV2Type'
import React, { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { Tooltip, useMediaQuery } from '@chakra-ui/react'
import ApplySkeleton from '@core/components/ApplyDialogV2/ApplySkeleton'
import CloseApplyModal from '@core/components/ApplyDialogV2/CloseApplyModal'
import clsx from 'clsx'
import ApplyNavBtn from '@core/components/ApplyDialogV2/ApplyNavBtn'
import { useApplyV2Context } from '@core/utils/contexts/ApplyV2ModalContext/ApplyV2ModalContext'
import { useTranslation } from 'react-i18next'
import './ApplyDialogV2.scss'

const baseClass = 'ApplyDialogV2'

interface ApplyDialogV2Props {
    className?: string
    children?: ReactNode
}

function ApplyDialogV2({ className = '', children }: ApplyDialogV2Props) {
    const {
        onOpenApply,
        isExpanded,
        isOpen,
        onCloseApply,
        onResizeApply,
        settingsData,
        settingsRefetch,
        settingsError,
        storageData,
        isSettingsFetching,
        isPostFetching,
        postData,
        onSelectStep,
    } = useApplyV2Context()
    const { t } = useTranslation()
    const nameRef = useRef<HTMLSpanElement>(null)
    const [isEllipsed, setIsEllipsed] = useState(false)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isDesktop] = useMediaQuery('(min-width: 1230px)')

    const sliderRef = useRef<HTMLDivElement>(null)
    // const [activeIndex, setActiveIndex] = useState(0)
    const [activeSlide, setActiveSlide] = useState<ApplyV2SettingsSectionName | null>(null)

    const navItems = useMemo(() => {
        const navSections = [
            ...Object.values(settingsData?.fields?.section || {}),
            {
                name: 'reviewApply' as ApplyV2SettingsSectionName,
                visible: true,
                required: true,
                order: 99,
            },
        ]
            .filter((it) => it.visible)
            .sort((a, b) => a.order - b.order)
        if (settingsData?.fields?.cv) {
            return [
                settingsData.fields.cv,
                { name: 'profile' as ApplyV2SettingsSectionName, visible: true, required: true, order: 0 },
                ...navSections,
            ]
        }
        return [
            { name: 'profile' as ApplyV2SettingsSectionName, order: 0, visible: true, required: true },
            ...navSections,
        ]
    }, [settingsData])

    const scrollToSlide = (slide: ApplyV2SettingsSectionName | null) => {
        if (!sliderRef.current) return
        const slideWidth = sliderRef.current.clientWidth
        const index = navItems.findIndex((item) => item.name === slide)
        sliderRef.current.scrollTo({
            left: index * slideWidth,
            behavior: 'instant',
        })
        setActiveSlide(slide)
    }

    // useEffect(() => {
    //     if (isOpen && isExpanded) {
    //         scrollToSlide(storageData?.currentStep ?? navItems[0].name)
    //     }
    // }, [storageData, navItems, isOpen, isExpanded])

    useEffect(() => {
        if (!isOpen || !isExpanded || !sliderRef.current) return

        const observer = new ResizeObserver(() => {
            scrollToSlide(storageData?.currentStep ?? navItems[0].name)
        })

        observer.observe(sliderRef.current)

        return () => {
            observer.disconnect()
        }
    }, [storageData, navItems, isOpen, isExpanded])

    useEffect(() => {
        const handleResize = () => {
            scrollToSlide(activeSlide)
        }

        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [activeSlide])

    const isLoading = isSettingsFetching || isPostFetching

    useEffect(() => {
        const el = nameRef.current
        if (el) {
            setIsEllipsed(el.scrollWidth > el.clientWidth)
        }
    }, [isOpen, isExpanded, postData])

    // useEffect(() => {
    //     const handleResize = () => {
    //         const el = nameRef.current
    //         if (el) {
    //             setIsEllipsed(el.scrollWidth > el.clientWidth)
    //         }
    //     }
    //
    //     window.addEventListener('resize', handleResize)
    //     handleResize()
    //
    //     return () => window.removeEventListener('resize', handleResize)
    // }, [])

    useEffect(() => {
        if (isOpen && isExpanded) {
            document.body.style.overflow = 'hidden'
        } else {
            document.body.style.overflow = ''
        }

        return () => {
            document.body.style.overflow = ''
        }
    }, [isOpen, isExpanded])

    if (!isOpen) return null

    return (
        <div
            className={clsx(baseClass, {
                [`${baseClass}__expanded`]: isExpanded,
                [`${baseClass}__opened`]: isOpen,
                [className]: !!className,
            })}
        >
            <CloseApplyModal isModalOpen={isModalOpen} onCloseApply={onCloseApply} setIsModalOpen={setIsModalOpen} />
            <div className={`${baseClass}__header`}>
                <ApplySkeleton isLoading={isLoading} style={{ width: '44px', height: '44px' }}>
                    <Tooltip
                        hasArrow
                        isDisabled={isLoading}
                        openDelay={500}
                        label={isExpanded ? 'Collapse' : 'Expand'}
                        placement={isExpanded ? 'top-start' : 'right-start'}
                        padding={'8px 16px'}
                        borderRadius={'4px'}
                        backgroundColor={'#19191A'}
                        fontWeight={600}
                        fontSize={'12px'}
                    >
                        <button onClick={onResizeApply} className={`${baseClass}__header__expand-btn`}>
                            {isExpanded ? (
                                <i className="fa-regular fa-chevron-down"></i>
                            ) : (
                                <i className="fa-regular fa-chevron-up"></i>
                            )}
                        </button>
                    </Tooltip>
                </ApplySkeleton>
                <div className={`${baseClass}__header__left`}>
                    <div className={`${baseClass}__header__title`}>
                        {!isEllipsed && (
                            <ApplySkeleton isLoading={isPostFetching}>
                                <span className={`${baseClass}__header__title--application`}>
                                    {t('1589', 'Application')}
                                </span>
                            </ApplySkeleton>
                        )}
                        <ApplySkeleton isLoading={isPostFetching} style={{ maxWidth: '240px' }}>
                            <span className={`${baseClass}__header__title--name`}>
                                <Tooltip
                                    hasArrow
                                    isDisabled={isLoading}
                                    openDelay={500}
                                    label={'We are searching for an exceptionally skilled Marketing Coordinator'}
                                    // placement={'right'}
                                    padding={'8px 16px'}
                                    borderRadius={'4px'}
                                    backgroundColor={'#19191A'}
                                    fontWeight={600}
                                    fontSize={'12px'}
                                >
                                    <span className={`${baseClass}__header__title--name-value`} ref={nameRef}>
                                        {storageData?.title}
                                    </span>
                                </Tooltip>
                            </span>
                        </ApplySkeleton>
                    </div>
                </div>

                {!isExpanded ? (
                    <ApplySkeleton isLoading={isLoading} style={{ width: '44px', height: '44px' }}>
                        <Tooltip
                            hasArrow
                            isDisabled={isLoading}
                            openDelay={500}
                            label={'Close application'}
                            placement={'left-start'}
                            padding={'8px 16px'}
                            borderRadius={'4px'}
                            backgroundColor={'#19191A'}
                            fontWeight={600}
                            fontSize={'12px'}
                        >
                            <button className={`${baseClass}__header__expand-btn`} onClick={() => setIsModalOpen(true)}>
                                <i className="fa-regular fa-xmark-large"></i>
                            </button>
                        </Tooltip>
                    </ApplySkeleton>
                ) : (
                    <div className={`${baseClass}__header__nav`}>
                        {/*<ApplyNavBtn>Default</ApplyNavBtn>*/}
                        {/*<ApplyNavBtn>Text</ApplyNavBtn>*/}
                        {/*<ApplyNavBtn state={'optional'}>Optional</ApplyNavBtn>*/}
                        {/*<ApplyNavBtn state={'active'}>Active</ApplyNavBtn>*/}
                        {/*<ApplyNavBtn state={'success'}>Success</ApplyNavBtn>*/}
                        {/*<ApplyNavBtn state={'error'}>Error</ApplyNavBtn>*/}
                        {/*<ApplyNavBtn state={'error'} isLoading={true}>*/}
                        {/*    Error*/}
                        {/*</ApplyNavBtn>*/}
                        {isSettingsFetching
                            ? Array.from({ length: 5 }).map((_, i) => (
                                  <ApplyNavBtn key={i} isLoading={isSettingsFetching} />
                              ))
                            : navItems.map((item) => (
                                  <ApplyNavBtn
                                      key={item.name}
                                      tooltipLabel={'Go to step'}
                                      isLoading={isSettingsFetching}
                                      onClick={() => onSelectStep(item.name)}
                                      state={item.name === activeSlide ? 'active' : 'default'}
                                  >
                                      {item.name}
                                  </ApplyNavBtn>
                              ))}
                    </div>
                )}
            </div>
            {/*<div className={clsx(`${baseClass}__content`, { [`${baseClass}__content--expanded`]: isExpanded })}>*/}
            {/*    <div className={`${baseClass}__slider-wrapper`} ref={sliderRef}>*/}
            {/*        {navItems.map((item, index) => (*/}
            {/*            <div key={item.name} className={`${baseClass}__slide`}>*/}
            {/*                <div> Слайд: {item.name}</div>*/}
            {/*            </div>*/}
            {/*        ))}*/}
            {/*    </div>*/}
            {/*</div>*/}

            <ApplyV2Content
                navItems={navItems}
                // activeIndex={activeIndex}
                currentStep={activeSlide}
                sliderRef={sliderRef}
                isExpanded={isExpanded}
                isLoading={isLoading}
                settingsData={settingsData}
            />
        </div>
    )
}

export default ApplyDialogV2
